import React, { useState } from 'react'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons'
import { ITEM_TYPE_OPTIONS, TABLE_COLUMNS, humanizeField, formatValue } from '../auditLogConstants'

const EVENT_BADGE_CLASSES = {
  create: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  update: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  destroy: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
}

const modelLabel = (itemType) => ITEM_TYPE_OPTIONS.find(option => option.value === itemType)?.label ?? itemType

export default function AuditLogEntry({ entry }) {
  const [expanded, setExpanded] = useState(false)
  const changedFields = Object.entries(entry.changes || {})
  const isExpandable = changedFields.length > 0

  const toggle = () => {
    if (isExpandable) setExpanded(!expanded)
  }

  return (
    <div className="border-b-1 border-rb-widget-border dark:border-rb-dark-mode-lighter last:border-b-0">
      {/* Row is a div (not a button) so the campaign name below can be a real, independently
          clickable link - a <button> can't contain other interactive/focusable elements. */}
      <div
        role="button"
        tabIndex={isExpandable ? 0 : -1}
        className={`w-full flex items-center gap-4 px-4 py-3 text-left transition-colors ${isExpandable ? 'cursor-pointer hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter' : ''}`}
        onClick={toggle}
        onKeyDown={e => {
          if (isExpandable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            toggle()
          }
        }}
      >
        <span className={`${TABLE_COLUMNS.chevron} text-rb-mat-grey dark:text-rb-dark-mode-text-grey`}>
          {isExpandable && (expanded ? <ChevronDown /> : <ChevronRight />)}
        </span>
        <span className={`${TABLE_COLUMNS.when} text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey`}>
          {format(new Date(entry.created_at), 'yyyy-MM-dd HH:mm')}
        </span>
        <span className={TABLE_COLUMNS.event}>
          <span className={`inline-flex items-center justify-center w-16 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${EVENT_BADGE_CLASSES[entry.event] ?? ''}`}>
            {entry.event}
          </span>
        </span>
        <span className={`${TABLE_COLUMNS.model} text-sm text-rb-black dark:text-white truncate`}>
          {modelLabel(entry.item_type)}
        </span>
        <span className={`${TABLE_COLUMNS.record} min-w-0`}>
          <span className="block text-sm font-semibold text-rb-black dark:text-white truncate">
            {entry.label}
          </span>
          {entry.campaign && (
            <Link
              to={`/project_manager/campaigns/${entry.campaign.id}/overview`}
              onClick={e => e.stopPropagation()}
              className="block text-xs text-rb-mat-grey dark:text-rb-dark-mode-text-grey hover:text-rb-green dark:hover:text-rb-green truncate"
            >
              {entry.campaign.name}
            </Link>
          )}
        </span>
        <span className={`${TABLE_COLUMNS.changedBy} text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey text-right truncate`}>
          {entry.whodunnit_name ?? 'System'}
        </span>
      </div>

      {expanded && isExpandable && (
        <div className="px-4 pb-4 pl-12">
          <ul className="space-y-1">
            {changedFields.map(([field, [before, after]]) => (
              <li key={field} className="text-sm">
                <span className="font-semibold text-rb-black dark:text-white">{humanizeField(field)}: </span>
                {entry.event === 'destroy' ? (
                  <span className="text-rb-mat-grey dark:text-rb-dark-mode-text-grey">{formatValue(before)}</span>
                ) : (
                  <>
                    <span className="text-rb-mat-grey dark:text-rb-dark-mode-text-grey line-through">{formatValue(before)}</span>
                    <span className="mx-1 text-rb-mat-grey dark:text-rb-dark-mode-text-grey">→</span>
                    <span className="text-rb-black dark:text-white">{formatValue(after)}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
